import {
  AlignCenterOutlined,
  AlignLeftOutlined,
  AlignRightOutlined,
  BoldOutlined,
  EnterOutlined,
  FontColorsOutlined,
  ItalicOutlined,
  OrderedListOutlined,
  StrikethroughOutlined,
  UnderlineOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { useTranslation } from '@framework/i18n';
import type { Editor } from '@tiptap/react';
import * as React from 'react';
import { useMemo } from 'react';
import { StyledSpace } from '../style/StyledSpace';
import { MenuGroup } from './MenuGroup';
import { RichTextButton } from './RichTextMenuButton';
import { RichTextMenuDivider } from './RichTextMenuDivider';
import { CiAlignJustify } from './buttons/IconsCustom';
import { RichTextLinkButtons } from './buttons/RichTextLinkButtons';

export interface IRichTextMenuBarProps {
  editor: Editor | null;
  menus?: MenuGroup[];
  extendedMenu?: (editor: Editor | null) => React.ReactNode;
  color?: string;
}

export function RichTextMenuBar(props: IRichTextMenuBarProps) {
  const { editor, menus } = props;
  const { t } = useTranslation();

  const extendedMenu = useMemo(
    () => props.extendedMenu?.(editor),
    [editor, props],
  );

  if (!editor) return null;

  const standard =
    !!menus?.find(m => m === 'default') || !menus || menus.length === 0;

  return (
    <StyledSpace wrap size={2}>
      {standard && (
        <>
          {/** Bold */}
          <RichTextButton
            icon={<BoldOutlined />}
            isActive={!!editor.isActive('bold')}
            title={t('inputs.rich-text-menu-bar.bold', 'Grassetto')}
            onClick={() => editor.chain().focus().toggleBold().run()}
            disabled={
              !editor.isEditable ||
              !editor.can().chain().focus().toggleBold().run()
            }
          />
          {/** Italic */}
          <RichTextButton
            icon={<ItalicOutlined />}
            isActive={!!editor.isActive('italic')}
            title={t('inputs.rich-text-menu-bar.italic', 'Corsivo')}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            disabled={
              !editor.isEditable ||
              !editor.can().chain().focus().toggleItalic().run()
            }
          />
          {/** Undeline */}
          <RichTextButton
            icon={<UnderlineOutlined />}
            isActive={!!editor.isActive('underline')}
            title={t('inputs.rich-text-menu-bar.underline', 'Sottolineato')}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            disabled={
              !editor.isEditable ||
              !editor.can().chain().focus().toggleUnderline().run()
            }
          />
          {/** Strike */}
          <RichTextButton
            icon={<StrikethroughOutlined />}
            isActive={!!editor.isActive('strike')}
            title={t('inputs.rich-text-menu-bar.strikethrough', 'Barrato')}
            onClick={() => editor.chain().focus().toggleStrike().run()}
            disabled={
              !editor.isEditable ||
              !editor.can().chain().focus().toggleStrike().run()
            }
          />
        </>
      )}
      {/** H1 */}
      {/* <RichTextButton
        icon={<BoldOutlined />}
        isActive={!!editor.isActive('heading', { level: 1 })}
        title="Titolo"
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        disabled={
           !editor.isEditable ||
             !editor.can().chain().focus().toggleHeading({ level: 1 }).run(
        }
      /> */}
      {/** H2 */}
      {/* <RichTextButton
        icon={<FontSizeOutlined />}
        isActive={!!editor.isActive('heading', { level: 2 })}
        title="Sottotitolo"
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        disabled={
           !editor.isEditable || !editor.can().chain().focus().toggleHeading({ level: 2 }).run()
        }
      /> */}

      {/** Link */}
      {!!menus?.find(m => m === 'link') && (
        <RichTextLinkButtons editor={editor} />
      )}

      {!!menus?.find(m => m === 'title') && (
        <>
          <RichTextMenuDivider />
          {/** H2 | Titolo */}
          <RichTextButton
            isActive={!!editor.isActive('heading', { level: 2 })}
            title={t('inputs.rich-text-menu-bar.title', 'Titolo')}
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 2 }).run()
            }
            disabled={
              !editor.isEditable ||
              !editor.can().chain().focus().toggleHeading({ level: 2 }).run()
            }
          >
            {t('inputs.rich-text-menu-bar.title', 'Titolo')}
          </RichTextButton>
        </>
      )}

      {!!menus?.find(m => m === 'align') && (
        <>
          <RichTextMenuDivider />
          <RichTextButton
            children={<AlignLeftOutlined />}
            isActive={!!editor.isActive({ textAlign: 'left' })}
            title={t(
              'inputs.rich-text-menu-bar.align-left',
              'Allinea a sinistra',
            )}
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            disabled={
              !editor.isEditable ||
              !editor.can().chain().focus().setTextAlign('left').run()
            }
          />
          <RichTextButton
            children={<AlignCenterOutlined />}
            isActive={!!editor.isActive({ textAlign: 'center' })}
            title={t(
              'inputs.rich-text-menu-bar.align-center',
              'Allinea al centro',
            )}
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            disabled={
              !editor.isEditable ||
              !editor.can().chain().focus().setTextAlign('center').run()
            }
          />
          <RichTextButton
            children={<AlignRightOutlined />}
            isActive={!!editor.isActive({ textAlign: 'right' })}
            title={t(
              'inputs.rich-text-menu-bar.align-right',
              'Allinea a destra',
            )}
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            disabled={
              !editor.isEditable ||
              !editor.can().chain().focus().setTextAlign('right').run()
            }
          />
          <RichTextButton
            children={<CiAlignJustify />}
            isActive={!!editor.isActive({ textAlign: 'justify' })}
            title={t(
              'inputs.rich-text-menu-bar.justify-text',
              'Giustifica testo',
            )}
            onClick={() => editor.chain().focus().setTextAlign('justify').run()}
            disabled={
              !editor.isEditable ||
              !editor.can().chain().focus().setTextAlign('justify').run()
            }
          />
        </>
      )}

      {!!menus?.find(m => m === 'list') && (
        <>
          <RichTextMenuDivider />
          {/** Liste Non Ordinate */}
          <RichTextButton
            icon={<UnorderedListOutlined />}
            isActive={!!editor.isActive('bulletList')}
            title={t('inputs.rich-text-menu-bar.unordered-list', 'Elenco')}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            disabled={
              !editor.isEditable ||
              !editor.can().chain().focus().toggleBulletList().run()
            }
          />
          {/** Liste Ordinate */}
          <RichTextButton
            icon={<OrderedListOutlined />}
            isActive={!!editor.isActive('orderedList')}
            title={t(
              'inputs.rich-text-menu-bar.ordered-list',
              'Elenco ordinato',
            )}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            disabled={
              !editor.isEditable ||
              !editor.can().chain().focus().toggleOrderedList().run()
            }
          />
        </>
      )}
      {!!menus?.find(m => m === 'return') && (
        <>
          <RichTextMenuDivider />
          {/** Accapo */}
          <RichTextButton
            icon={<EnterOutlined />}
            title={t('inputs.rich-text-menu-bar.hard-break', 'Accapo')}
            onClick={() => {
              editor.chain().focus().setHardBreak().run();
            }}
            disabled={!editor.isEditable}
          />
        </>
      )}

      {!!menus?.find(m => m === 'color') && (
        <>
          <RichTextMenuDivider />
          {/** Colore */}
          <RichTextButton
            icon={<FontColorsOutlined />}
            isActive={!!editor.isActive('surveyColor')}
            title={t('inputs.rich-text-menu-bar.color', 'Colore')}
            onClick={() => {
              editor.chain().focus().toggleSurveyColor().run();
            }}
            disabled={!editor.isEditable}
          />
        </>
      )}

      {extendedMenu}
    </StyledSpace>
  );
}
